import type { ReactNode } from 'react'
import { CheckCircle2, Mail, Radio, Send } from 'lucide-react'
import { CogoWordmark } from '@/components/cogo-logo'
import { LanguageSwitcher } from '@/pages/cogo-marketing/LanguageSwitcher'
import { useLocale } from '@/pages/cogo-marketing/i18n'
import { getRenewalEntryCopy } from '@/pages/cogo-marketing/renewal-entry-copy'
import { useStudioLocale } from '@/i18n/use-studio-locale'

/**
 * The skip link's target and the `<main>` landmark's accessible name, the two
 * halves of one jump. Held as constants so a rename cannot break the link
 * silently — the same shape `DASHBOARD_CONTENT_ID` has in
 * `studio-dashboard-shell.tsx`, whose skip link this one is modelled on.
 */
export const STUDIO_ENTRY_CONTENT_ID = 'studio-entry-content'
const STUDIO_ENTRY_TITLE_ID = 'studio-entry-title'

export interface StudioEntryReport {
  kicker: string
  beforePrimary: string
  primary: string
  between: string
  secondary: string
  afterSecondary: string
  approve: string
  review: string
  replyPlaceholder: string
  metrics: readonly [string, string, string]
}

interface StudioEntryShellProps {
  children: ReactNode
  testId?: string
  eyebrow?: string
  title: string
  description: string
  statusLabel?: string
  statusDescription?: string
  tone?: 'neutral' | 'success' | 'warning' | 'danger'
  report?: StudioEntryReport
  footer?: ReactNode
}

export function StudioEntryShell({
  children,
  testId,
  eyebrow,
  title,
  description,
  statusLabel = 'Living applications. Living documents.',
  statusDescription = 'Start with a request, then keep shaping the work as it changes.',
  tone = 'neutral',
  report,
  footer,
}: StudioEntryShellProps) {
  // UI-VERIFY LOCALE-01: the default footer used to be a hardcoded Korean
  // literal, so the checkout success/failure/pending pages — which render in
  // English and ship a LanguageSwitcher in the same header — always showed
  // Korean at the bottom regardless of the selected locale. The strings
  // already existed per-locale in renewal-entry-copy.ts (the auth page passes
  // its own footer built from them); this only stops the shell from ignoring
  // them when no footer prop is supplied.
  const { locale } = useLocale()
  const entryCopy = getRenewalEntryCopy(locale).auth
  // The accessibility surface of this shell. It is PRE-AUTH, so #2324's sweep
  // — derived from `STUDIO_ROUTE_REGISTRY`, i.e. the authenticated `/studio/*`
  // routes — could not reach it, and its two `aria-label`s stayed English on a
  // `lang="ko"` document. On the appliance this shell is the FIRST screen.
  const { strings } = useStudioLocale()
  return (
    <main
      data-testid={testId}
      aria-labelledby={STUDIO_ENTRY_TITLE_ID}
      className="grid min-h-dvh bg-white text-[#102033] lg:grid-cols-[560px_minmax(0,1fr)]"
    >
      {/* Invisible until it takes focus — which is why a rendered-text sweep
          cannot see it, and why the gate reads it off the DOM by href. */}
      <a
        href={`#${STUDIO_ENTRY_CONTENT_ID}`}
        className="sr-only z-[1000] rounded-md bg-background px-4 py-2 text-sm font-semibold text-foreground shadow focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {strings.a11y.skipToMainContent}
      </a>
      <section className="flex min-h-dvh flex-col px-8 py-8 sm:px-16 sm:py-12">
        <div className="flex items-center justify-between gap-5">
          {/* UI-VERIFY LOGIN-BRAND-01: the lockup (chevron mark + wordmark,
              one flattened SVG asset — icon and text can't drift apart or
              misalign since they're a single image) rendered at size 28, the
              smallest of any primary chrome placement of this asset in the
              app (dashboard header: 32-36px, marketing nav: 40px — none of
              which vary by breakpoint either; `CogoWordmark` always sets
              `display: inline-block` via inline style, which unconditionally
              wins over any `hidden`/breakpoint-prefixed Tailwind class, so a
              show/hide-per-breakpoint approach cannot work here). Bump to
              32px to match the dashboard header's baseline chrome sizing,
              and add `shrink-0`/`flexShrink: 0` (present on the sibling
              CogoLogoBrand but missing here) so the lockup can never be
              compressed inside this flex row at narrow (375px) viewports. */}
          <a
            href="/"
            className="inline-flex w-fit shrink-0 items-center"
            aria-label={strings.a11y.entryBrandHome}
          >
            <CogoWordmark size={32} style={{ flexShrink: 0 }} />
          </a>
          <LanguageSwitcher />
        </div>
        <div id={STUDIO_ENTRY_CONTENT_ID} tabIndex={-1} className="my-auto w-full max-w-[432px] py-10">
          {eyebrow && (
            <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#0369A1]">{eyebrow}</p>
          )}
          <h1
            id={STUDIO_ENTRY_TITLE_ID}
            className="text-[32px] font-extrabold leading-tight tracking-[-0.03em] text-[#102033] sm:text-[34px]"
          >
            {title}
          </h1>
          <p className="mt-3 text-[15px] leading-6 text-[#64748B]">{description}</p>
          <div className="mt-9">{children}</div>
        </div>
        <div className="text-xs leading-5 text-[#94A3B8]" data-testid="studio-entry-footer">
          {footer ?? (
            <>
              {entryCopy.newQuestion}{" "}
              <span className="font-semibold text-[#0369A1]">
                {entryCopy.tryFirst}
              </span>{" "}
              — {entryCopy.accountNote}
            </>
          )}
        </div>
      </section>

      <aside
        className="hidden min-h-dvh bg-[#0B1220] px-12 py-12 text-[#F8FAFC] lg:flex lg:flex-col lg:justify-center xl:px-16"
        aria-label={strings.a11y.entryReport}
      >
        <div className="mx-auto w-full max-w-[620px]">
          {report ? (
            <>
              <p className="mb-5 text-[13px] font-semibold text-[#64748B]">{report.kicker}</p>
              <div className="overflow-hidden rounded-[14px] border border-[#1E293B] bg-[#101A2E]">
                <div className="flex flex-col gap-4 px-5 py-5 sm:px-6">
                  <div className="flex max-w-[92%] items-start gap-3 text-[13px] leading-6 text-[#E2E8F0]">
                    <img
                      src="/images/brand/coogo_ff_mark.svg"
                      alt=""
                      className="mt-1 h-[15px] w-6 shrink-0 object-contain"
                    />
                    <p>
                      {report.beforePrimary}
                      <strong className="text-[#2DD4BF]">{report.primary}</strong>
                      {report.between}
                      <strong className="text-[#C4B5FD]">{report.secondary}</strong>
                      {report.afterSecondary}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-0 sm:pl-9">
                    <span className="rounded-lg bg-[#0EA5E9] px-4 py-2 text-xs font-semibold text-white">
                      {report.approve}
                    </span>
                    <span className="rounded-lg border border-[#334155] px-4 py-2 text-xs font-semibold text-[#94A3B8]">
                      {report.review}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3 border-t border-[#1E293B] px-4 py-3 text-[13px] text-[#64748B]">
                  <span className="flex-1">{report.replyPlaceholder}</span>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#0EA5E9] text-white">
                    <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </div>
              </div>
              <div className="mt-4 grid gap-3 text-xs text-[#64748B] xl:grid-cols-3">
                <span className="inline-flex items-center gap-2">
                  <Radio className="h-3.5 w-3.5 text-[#2DD4BF]" aria-hidden="true" />
                  {report.metrics[0]}
                </span>
                <span className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#38BDF8]" aria-hidden="true" />
                  {report.metrics[1]}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-[#C4B5FD]" aria-hidden="true" />
                  {report.metrics[2]}
                </span>
              </div>
            </>
          ) : (
            <div
              className={`rounded-[14px] border border-[#243147] bg-[#111D31] p-6 ${
                tone === 'danger'
                  ? 'border-l-4 border-l-rose-400'
                  : tone === 'warning'
                    ? 'border-l-4 border-l-amber-400'
                    : tone === 'success'
                      ? 'border-l-4 border-l-[#2DD4BF]'
                      : 'border-l-4 border-l-[#38BDF8]'
              }`}
            >
              <p className="text-sm font-bold text-white">{statusLabel}</p>
              <p className="mt-2 text-sm leading-6 text-[#94A3B8]">{statusDescription}</p>
            </div>
          )}
          <p className="sr-only">
            {statusLabel}. {statusDescription}
          </p>
        </div>
      </aside>
    </main>
  );
}
